import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import s from './Button.module.css';
import { Icon } from './Icon';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerGhost';
type Size = 'sm' | 'md' | 'lg';

interface Common {
  variant?: Variant;
  size?: Size;
  icon?: string;
  iconRight?: string;
  block?: boolean;
  loading?: boolean;
  children?: ReactNode;
}

function cls(variant: Variant, size: Size, block?: boolean, iconOnly?: boolean, extra?: string) {
  return [s.btn, s[variant], size !== 'md' && s[size], block && s.block, iconOnly && s.iconOnly, extra].filter(Boolean).join(' ');
}

export const Button = forwardRef<HTMLButtonElement, Common & ButtonHTMLAttributes<HTMLButtonElement>>(function Button(
  { variant = 'secondary', size = 'md', icon, iconRight, block, loading, children, className, disabled, type = 'button', ...rest }, ref,
) {
  const iconSize = size === 'sm' ? 15 : 17;
  return (
    <button ref={ref} type={type} className={cls(variant, size, block, !children, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className={s.spinner} aria-hidden /> : icon ? <Icon name={icon} size={iconSize} /> : null}
      {children}
      {iconRight && !loading ? <Icon name={iconRight} size={iconSize} /> : null}
    </button>
  );
});

export function ButtonLink({ variant = 'secondary', size = 'md', icon, iconRight, block, children, className, ...rest }: Common & LinkProps) {
  const iconSize = size === 'sm' ? 15 : 17;
  return (
    <Link className={cls(variant, size, block, !children, className)} {...rest}>
      {icon ? <Icon name={icon} size={iconSize} /> : null}
      {children}
      {iconRight ? <Icon name={iconRight} size={iconSize} /> : null}
    </Link>
  );
}

export function ButtonA({ variant = 'secondary', size = 'md', icon, iconRight, block, children, className, ...rest }: Common & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const iconSize = size === 'sm' ? 15 : 17;
  return (
    <a className={cls(variant, size, block, !children, className)} {...rest}>
      {icon ? <Icon name={icon} size={iconSize} /> : null}
      {children}
      {iconRight ? <Icon name={iconRight} size={iconSize} /> : null}
    </a>
  );
}
